import _ from 'lodash'
import { PureComponent } from 'react'
import { connect } from 'react-redux'
import DimensionsDisplay from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/PreviewPanel/PreviewItem/DimensionsDisplay'
import { THREADS_KEY } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'

import { openChatDriftDFMFeedback } from '~/redux-modules/drift'
import Box from '~/core/Box'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import MeasurePanel from '~/apps/consumer/components/MeasurePanel'
import getDimensionsDisplayWithUnits from '~/lib/getDimensionsDisplayWithUnits'
import ModelViewer from '~/core/ModelViewer'
import { DFM_INSPECTION_MODE } from '~/redux-modules/qmr-viewer/constants'
import {
    anyCommentsOrDfmSelected,
    getViewerInstance,
    getSelectedPaletteColor,
    getPaletteColors,
    getMeasurementUnits,
    getModelViewerConfig,
    getAnnotatable,
    getSelectedPatchHashes,
} from '~/redux-modules/qmr-viewer/selectors'
import {
    createViewerInstance,
    hoverPatch,
    patchClick,
    resetView,
    setTransparency,
    setCrossSectionView,
    hoverCrossSectionTool,
    setViewerInteractionMode,
    setInteractionModeColor,
    setMeasurementUnits,
    zoomCamera,
    panCamera,
    rotateCamera,
    resetViewerState,
    patchCtrlClick,
} from '~/redux-modules/qmr-viewer/actions'
import {
    SUCCESSFULLY_INITIALIZED,
    FAILED_TO_INITIALIZE,
} from '~/core/ModelViewer/logic'
import Button from '~/core/Button'
import customWithRouter from '~/lib/customWithRouter'

import ControlsBar from './ControlsBar'
import InfoBar from './InfoBar'
import styles from './styles.css'
import { getNewCameraConfig } from './logic'

const PLACEMENT = 'builder-model-viewer'

class BuilderModelViewer extends PureComponent {
    state = {
        viewerState: '',
        isTransparent: false,
        isOpaque: false,
    }

    componentDidMount() {
        this.props.dispatch(
            setViewerInteractionMode({
                mode: this.props.defaultMode || DFM_INSPECTION_MODE,
                // Initialize as annotatable only if the consumer of component wants the viewer to be always annotatable
                annotatable: Boolean(this.props.alwaysAnnotatable),
            }),
        )
    }

    onHoverPatch = ({ patchHash }) => {
        this.props.dispatch(hoverPatch(patchHash))
    }

    onToggleTransparent = () => {
        const { transparent } = this.props.modelViewer

        this.props.dispatch(setTransparency(!transparent))
    }

    setTransparent(value) {
        if (!this.state.isTransparent) {
            this.props.dispatch(setTransparency(!value))
            this.setState({ isTransparent: true })
        }
    }
    setOpaque(value) {
        if (!this.state.isOpaque) {
            this.props.dispatch(setTransparency(!value))
            this.setState({ isOpaque: true })
        }
    }

    onResetView = () => {
        this.props.dispatch(resetView())
    }

    onPatchClick = patchClickEvent => {
        this.props.dispatch(patchClick(patchClickEvent))
    }

    onPatchCtrlClick = patchClickEvent => {
        this.props.dispatch(patchCtrlClick(patchClickEvent))
    }

    onCameraZoom = () => {
        const { viewer, cameraConfig } = this.props
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        this.props.dispatch(zoomCamera(newCameraConfig))
    }

    onCameraPan = () => {
        const { viewer, cameraConfig } = this.props
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        this.props.dispatch(panCamera(newCameraConfig))
    }

    onCameraRotation = () => {
        const { viewer, cameraConfig } = this.props
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        this.props.dispatch(rotateCamera(newCameraConfig))
    }

    onViewCubeClick = () => {
        const { viewer, cameraConfig } = this.props
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        this.props.dispatch(rotateCamera(newCameraConfig))
    }

    onSetColor = color => {
        return () => {
            this.props.dispatch(setInteractionModeColor(color))
        }
    }

    onViewerInstantiate = viewer => {
        this.props.dispatch(createViewerInstance({ viewer }))
    }

    setInteractionMode = interactionMode => {
        this.props.dispatch(setViewerInteractionMode({ mode: interactionMode }))
    }

    setCrossSectionView = view => {
        this.props.dispatch(setCrossSectionView(view))
    }

    onSelectMeasurementUnits = units => {
        this.props.dispatch(setMeasurementUnits(units))
    }

    onCrossSectionToolHover = ({ tool = null }) => {
        if (tool) {
            this.props.dispatch(hoverCrossSectionTool(tool.name))
        }

        return null
    }

    dimensions = () => {
        const boundingBox = this.props.partRevision?.boundingBox

        return getDimensionsDisplayWithUnits(boundingBox, 'mm')
    }
    onOpenChat = msg => {
        const { dispatch } = this.props

        dispatch(openChatDriftDFMFeedback(msg))
    }

    getViewerStatus = status => {
        this.setState({ viewerState: status })
    }

    onViewerUnmount = () => {
        return this.props.dispatch(resetViewerState())
    }

    render() {
        const {
            cameraConfig,
            annotationConfigs,
            dfmFeatureConfigs,
            partRotation,
            transparent,
            patchConfigs,
            crossSectionConfig,
            customP2PPoints,
            selectedPoints,
        } = this.props.modelViewer
        const { viewerState } = this.state

        const {
            partRevision,
            interactionMode,
            hideControls,
            measurements,
            units,
            selectedPaletteColor,
            paletteColors,
            isConfigurationModal,
            selectedTab,
            attachment,
            router: { location },
        } = this.props
        const boundingBox = partRevision?.boundingBox

        const urlFor2dViewer = `${location.pathname}?view=drawing`
        const pathValue = location.pathname.split('/')

        if (pathValue[pathValue.length - 1] === THREADS_KEY) {
            this.setTransparent(false)
            this.setState({ isOpaque: false })
        } else {
            this.setOpaque(true)
            this.setState({ isTransparent: false })
        }
        const measurementPanelValues = {
            measures: measurements,
            units,
        }

        return (
            <>
                <ModelViewer
                    sceneBundle={partRevision?.sceneBundle}
                    cameraConfig={cameraConfig}
                    patchConfigs={patchConfigs}
                    annotationConfigs={annotationConfigs}
                    dfmFeatureConfigs={dfmFeatureConfigs}
                    transparent={transparent}
                    partRotation={partRotation}
                    crossSectionConfig={crossSectionConfig}
                    isConfigurationModal={isConfigurationModal}
                    selectedTab={selectedTab}
                    selectedPoints={selectedPoints}
                    customP2PPoints={customP2PPoints}
                    onPatchHover={this.onHoverPatch}
                    onPatchClick={this.onPatchClick}
                    onPatchCtrlClick={this.onPatchCtrlClick}
                    onViewerInstantiate={this.onViewerInstantiate}
                    onCameraZoom={this.onCameraZoom}
                    onCameraPan={this.onCameraPan}
                    onCameraRotation={this.onCameraRotation}
                    onCrossSectionToolHover={this.onCrossSectionToolHover}
                    onBackgroundClick={this.onBackgroundClick}
                    openChat={this.onOpenChat}
                    getViewerStatus={this.getViewerStatus}
                    onViewCubeClick={this.onViewCubeClick}
                    onUnmount={this.onViewerUnmount}
                />
                {hideControls || (
                    <ControlsBar
                        interactionMode={interactionMode}
                        crossSectionConfig={crossSectionConfig}
                        setInteractionMode={this.setInteractionMode}
                        setCrossSectionView={this.setCrossSectionView}
                        setColor={this.onSetColor}
                        selectedPaletteColor={selectedPaletteColor}
                        paletteColors={paletteColors}
                    />
                )}
                {(hideControls && !measurements.length) || (
                    <MeasurePanel
                        onSelectMeasurementUnits={this.onSelectMeasurementUnits}
                        values={measurementPanelValues}
                        panel={{}}
                    />
                )}
                {isConfigurationModal ? (
                    viewerState === SUCCESSFULLY_INITIALIZED && (
                        <InfoBar
                            transparentMode={transparent}
                            onToggleTransparent={this.onToggleTransparent}
                            onReset={this.onResetView}
                            dimensions={this.dimensions()}
                        />
                    )
                ) : (
                    <InfoBar
                        transparentMode={transparent}
                        onToggleTransparent={this.onToggleTransparent}
                        onReset={this.onResetView}
                        dimensions={this.dimensions()}
                    />
                )}
                {isConfigurationModal &&
                    viewerState === SUCCESSFULLY_INITIALIZED &&
                    attachment && (
                        <Button
                            name="view-2d-drawing-button"
                            placement={PLACEMENT}
                            className={styles.view2DButtonStyle}
                            to={urlFor2dViewer}
                            data-test-target="view-2d-drawing-button"
                        >
                            View 2D drawing
                        </Button>
                    )}
                {isConfigurationModal && viewerState === FAILED_TO_INITIALIZE && (
                    <Box
                        direction="col"
                        alignContent="end"
                        className={styles.dimensionStyle}
                    >
                        <Text
                            color={getColor('blackAlpha65')}
                            fontSize="14px"
                            fontWeight={300}
                        >
                            Dimensions
                        </Text>
                        <DimensionsDisplay boundingBox={boundingBox} />
                    </Box>
                )}
            </>
        )
    }
}

function mapStateToProps(state, ownProps) {
    const holes = ownProps.holes || []
    const dfmFeedbackArr = ownProps.dfmFeedback || []
    const comments = ownProps.annotations || []
    const anyCardSelected = anyCommentsOrDfmSelected(state)
    const hideControls =
        !ownProps.alwaysAnnotatable &&
        (!getAnnotatable(state) || anyCardSelected)
    const viewer = getViewerInstance(state)
    const units = getMeasurementUnits(state)
    const selectedPatchHashes = getSelectedPatchHashes(state)
    const selectedPaletteColor = getSelectedPaletteColor(state)
    const paletteColors = getPaletteColors(state)
    const measurements = []
    const modelViewerConfig = getModelViewerConfig(state, {
        holes,
        dfmFeedbackArr,
        comments,
    })
    const {
        cameraConfig,
        partRotation,
        annotations,
        patchConfigs,
        transparent,
        dfmFeatureConfigs,
        interactionMode,
        crossSectionConfig,
        selectedPoints,
        customP2PPoints,
    } = modelViewerConfig

    if (viewer) {
        const measurementInfo = viewer.getMeasurement({
            patchHashes: selectedPatchHashes,
            points: selectedPoints,
            units,
        })

        if (measurementInfo) {
            measurements.push(measurementInfo)
        }
    }

    return {
        interactionMode,
        hideControls,
        selectedPaletteColor,
        paletteColors,
        measurements,
        units,
        modelViewer: {
            cameraConfig,
            dfmFeatureConfigs,
            patchConfigs,
            annotationConfigs: annotations,
            partRotation,
            transparent,
            crossSectionConfig,
            customP2PPoints,
            selectedPoints,
        },
        viewer,
    }
}

export default _.flowRight(
    customWithRouter,
    connect(mapStateToProps),
)(BuilderModelViewer)
